/*
 * LegStyle cover viewer: <legstyle-cover-viewer>
 *
 * Purpose: the 3D cover shared by "Choose Your Cover" (sections/legstyle-choose-cover.liquid) and
 * "Make It Yours" (sections/legstyle-make-it-yours.liquid). Ported from the prototypes in
 * 01_Knowledge\10_Website\Make_It_Yours\ (make_it_yours.src.html and choose_cover.src.html, 4 to 5 Oct
 * 2026). The cover shape, lights, relief depth and mapping are the prototypes' measured values; do not
 * change them without Product Development.
 *
 * HTML first: the element's markup (snippets/legstyle-cover-stage.liquid) shows a still image. This module
 * loads three.js after the page has loaded and draws the cover over it. If the import fails, WebGL is
 * missing or the context is lost, the still image stays and the page keeps working.
 *
 * Section reloads (theme editor): connectedCallback sets everything up, disconnectedCallback tears it
 * down (renderer, observers, listeners, animation loop). init() and teardown() are public for tests.
 *
 * three.js: a pinned runtime import from cdn.jsdelivr.net at three@0.165.0, the same as
 * sections/legstyle-3d-preview.liquid. data-three-src and data-orbit-src override the two URLs (for a
 * vendored theme asset at launch, or to simulate a failed import in tools/local_preview).
 *
 * API used by the page modules (all safe to call before 3D is ready):
 *   viewer.ready                      Promise<boolean>: true once 3D shows, false if it fell back to the still
 *   viewer.setColour(hex)
 *   viewer.setStill(src, alt)
 *   viewer.showMap({ url, base, amp, still, alt }) -> 'shown' | 'still' | 'superseded' | 'error'
 *       loads a range design's relief map; only the latest call may change the cover
 *   viewer.setRelief(heights, size, shift)   a drawing's height map (Make It Yours); cancels a pending map
 */

const DEFAULT_THREE = 'https://cdn.jsdelivr.net/npm/three@0.165.0/+esm';
const DEFAULT_ORBIT = 'https://cdn.jsdelivr.net/npm/three@0.165.0/examples/jsm/controls/OrbitControls.js/+esm';

const RELIEF = 1.3;      // mm, as the template's Displace modifier (strength 1.3 mm); Astra review, 4 Oct 2026
const H_MM = 360;        // cover height, between the simple shape (330) and Andre's cover (425)
// Light levels, measured 4 Oct 2026: the middle of the cover renders within a few shades of the swatch
// on the front, outer side and back (Tan 196,138,95 renders 191 to 196, 131 to 135, 84 to 89).
const KEY = 2.7, AMB = 0.95;
const EL = 0.12;         // the camera's slight downward look (radians); on compact phones the turn is locked to it

/* ---------- the cover shape (prototype, unchanged) ---------- */
// t: 0 at the knee, 1 at the ankle. Radius in mm.
const PROFILE = [[0, 49], [0.16, 53], [0.32, 53.5], [0.55, 45], [0.8, 35], [1, 31]];
function profile(t) {
  const P = PROFILE; let i = 0;
  while (i < P.length - 2 && t > P[i + 1][0]) i++;
  const p0 = P[Math.max(i - 1, 0)], p1 = P[i], p2 = P[i + 1], p3 = P[Math.min(i + 2, P.length - 1)];
  const s = (t - p1[0]) / (p2[0] - p1[0]);
  const m1 = (p2[1] - p0[1]) / Math.max(p2[0] - p0[0], 1e-6) * (p2[0] - p1[0]);
  const m2 = (p3[1] - p1[1]) / Math.max(p3[0] - p1[0], 1e-6) * (p2[0] - p1[0]);
  const s2 = s * s, s3 = s2 * s;
  return (2 * s3 - 3 * s2 + 1) * p1[1] + (s3 - 2 * s2 + s) * m1 + (-2 * s3 + 3 * s2) * p2[1] + (s3 - s2) * m2;
}
// Laid out like the real (right-leg) cover: front (+Z) at u 0.75, outer side (-X) at u 0.5.
// Pattern UVs (the template's, checked 4 Oct 2026): u 0 = inner side, 0.25 = back, 0.5 = outer side, 0.75 = front.
function phiOf(u) { return 2 * Math.PI * (u - 0.75); }
// Top trimline, seen from the side: an S-curve, level at the front and level at the back (Andre's sketch, 4 Oct).
function topY(phi) { const s = (1 - Math.cos(phi)) / 2; return H_MM - 36 * s * s * (3 - 2 * s); }
// Andre's cover (website rendering file, measured 4 Oct 2026) by height t: half-width, front reach, back reach, mm.
const REAL = [[0.05, 57.8, 50.2, 45.1], [0.15, 58.2, 60.3, 69.0], [0.25, 63.0, 58.1, 77.2], [0.35, 67.0, 59.1, 83.5],
  [0.45, 65.2, 55.7, 82.7], [0.55, 58.2, 52.8, 73.3], [0.65, 50.5, 47.1, 66.2], [0.75, 44.2, 44.1, 60.1],
  [0.85, 38.1, 42.7, 55.9], [0.95, 34.6, 46.3, 55.1]];
const BLEND = 0.4;   // 0 = the simple first shape, 1 = Andre's cover. He prefers the simple one, so lean that way.
function realAt(t, col) {
  const R = REAL, T = Math.min(0.95, Math.max(0.05, t)); let i = 0;
  while (i < R.length - 2 && T > R[i + 1][0]) i++;
  const p0 = R[Math.max(i - 1, 0)], p1 = R[i], p2 = R[i + 1], p3 = R[Math.min(i + 2, R.length - 1)];
  const h = p2[0] - p1[0], s = (T - p1[0]) / h;
  const m1 = (p2[col] - p0[col]) / Math.max(p2[0] - p0[0], 1e-6) * h;
  const m2 = (p3[col] - p1[col]) / Math.max(p3[0] - p1[0], 1e-6) * h;
  const s2 = s * s, s3 = s2 * s;
  return (2 * s3 - 3 * s2 + 1) * p1[col] + (s3 - 2 * s2 + s) * m1 + (-2 * s3 + 3 * s2) * p2[col] + (s3 - s2) * m2;
}
function dims(t) {                          // [half-width, front, back] at height t (0 knee, 1 ankle)
  const p = profile(t), calf = Math.exp(-Math.pow((t - 0.3) / 0.17, 2));
  const mine = [p, p * 0.98, p * 1.03 * (1 + 0.085 * calf)];
  return mine.map((m, i) => (1 - BLEND) * m + BLEND * realAt(t, i + 1));
}
function pointAt(u, y, inset) {
  const phi = phiOf(u), t = 1 - y / H_MM, [w, f, b] = dims(t), c = Math.cos(phi);
  return [(w - inset) * Math.sin(phi), y, ((c >= 0 ? f : b) - inset) * c];
}

function buildSurface(THREE, inset, nu, nv) {
  const pos = new Float32Array((nu + 1) * (nv + 1) * 3), uv = new Float32Array((nu + 1) * (nv + 1) * 2);
  let k = 0, m = 0;
  for (let j = 0; j <= nv; j++) {
    const s = j / nv;
    for (let i = 0; i <= nu; i++) {
      const u = i / nu, y = (1 - s) * topY(phiOf(u));
      const p = pointAt(u, y, inset);
      pos[k++] = p[0]; pos[k++] = p[1]; pos[k++] = p[2];
      uv[m++] = u; uv[m++] = y / H_MM;
    }
  }
  const idx = new Uint32Array(nu * nv * 6); let q = 0;
  for (let j = 0; j < nv; j++) for (let i = 0; i < nu; i++) {
    const a = j * (nu + 1) + i, b = a + 1, c = a + nu + 1, d = c + 1;
    idx[q++] = a; idx[q++] = c; idx[q++] = b; idx[q++] = b; idx[q++] = c; idx[q++] = d;   // outward-facing (right leg)
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
  g.setAttribute('uv', new THREE.BufferAttribute(uv, 2));
  g.setIndex(new THREE.BufferAttribute(idx, 1));
  g.computeVertexNormals();
  g.userData.seam = { nu, nv }; fixSeam(g);
  return g;
}
// The first and last column of the grid are the same place on the leg; share their normals so no join shows.
function fixSeam(g) {
  const s = g.userData.seam; if (!s) return;
  const n = g.attributes.normal.array, w = s.nu + 1;
  for (let j = 0; j <= s.nv; j++) {
    const a = j * w * 3, b = (j * w + s.nu) * 3;
    let x = n[a] + n[b], y = n[a + 1] + n[b + 1], z = n[a + 2] + n[b + 2];
    const l = Math.hypot(x, y, z) || 1; x /= l; y /= l; z /= l;
    n[a] = n[b] = x; n[a + 1] = n[b + 1] = y; n[a + 2] = n[b + 2] = z;
  }
  g.attributes.normal.needsUpdate = true;
}
function buildRim(THREE, atTop, nu) {
  const pos = [], idx = [];
  for (let i = 0; i <= nu; i++) {
    const u = i / nu, y = atTop ? topY(phiOf(u)) : 0;
    pos.push(...pointAt(u, y, 0), ...pointAt(u, y, 3.2));
  }
  for (let i = 0; i < nu; i++) { const a = 2 * i; idx.push(a, a + 1, a + 2, a + 1, a + 3, a + 2); }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx); g.computeVertexNormals();
  return g;
}

/* ---------- helpers ---------- */
const threeCache = new Map();
function loadThree(threeSrc, orbitSrc) {
  const key = threeSrc + '|' + orbitSrc;
  if (!threeCache.has(key)) {
    const p = Promise.all([import(threeSrc), import(orbitSrc)])
      .then(([T, O]) => ({ THREE: T, OrbitControls: O.OrbitControls }));
    p.catch(() => threeCache.delete(key));          // a later page view may try again
    threeCache.set(key, p);
  }
  return threeCache.get(key);
}
// Phones: a lighter mesh and a smaller normal map (memory care, Tasks.md "How it is built").
function isPhone() {
  try { return window.matchMedia('(pointer: coarse)').matches && Math.min(screen.width, screen.height) < 820; }
  catch { return false; }
}
function whenIdle(fn) {
  if ('requestIdleCallback' in window) { const id = requestIdleCallback(fn, { timeout: 1500 }); return () => cancelIdleCallback(id); }
  const id = setTimeout(fn, 200); return () => clearTimeout(id);
}

class LegStyleCoverViewer extends HTMLElement {
  connectedCallback() { this.init(); }
  disconnectedCallback() { this.teardown(); }

  /* ready: a fresh promise per init, so a section reload starts clean */
  get ready() {
    if (!this._ready) this._ready = new Promise((res) => { this._resolveReady = res; });
    return this._ready;
  }

  init() {
    if (this._live) return;
    this._live = true;
    this._gen = (this._gen || 0) + 1;
    this._ac = new AbortController();
    this._ready = null; void this.ready;
    this._seq = this._seq || 0;
    this._relief = this._relief || null;          // { heights, size, shift } or null (flat)
    this._hex = this._hex || this.dataset.colour || '#dc9e7f';
    this._mode = 'loading';
    this._builds = 0;
    this.dataset.state = 'loading';
    this._stage = this.querySelector('.lsc-stage');
    this._still = this.querySelector('.lsc-still');
    this._hint = this.querySelector('[data-ls-hint]');
    this._views = this.querySelector('[data-ls-views]');
    this._phone = isPhone();
    this._reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const signal = this._ac.signal;

    // artwork protection, as the prototype: no right-click save on the cover
    this._stage.addEventListener('contextmenu', (e) => e.preventDefault(), { signal });
    if (this._views) this._views.addEventListener('click', (e) => this._onView(e), { signal });

    if (this.dataset.threeSrc === 'off') { this._toStill('off'); return; }
    // Still image first; three.js only after the page has loaded and the browser is idle.
    const go = () => { this._cancelIdle = whenIdle(() => this._start3d()); };
    if (document.readyState === 'complete') go();
    else window.addEventListener('load', go, { once: true, signal });
  }

  teardown() {
    if (!this._live) return;
    this._live = false;
    this._gen++;
    if (this._cancelIdle) { this._cancelIdle(); this._cancelIdle = null; }
    this._ac.abort();
    this._teardown3d();
    if (this._resolveReady) this._resolveReady(false);
  }

  /* ---------- public API ---------- */
  setColour(hex) {
    this._hex = hex;
    if (this._g) { for (const m of this._g.mats) m.color.set(hex); this._invalidate(); }
  }
  setStill(src, alt) {
    if (!this._still || !src) return;
    if (this._still.getAttribute('src') !== src) {
      this._still.removeAttribute('srcset');
      this._still.src = src;
    }
    if (alt != null) this._still.alt = alt;
  }
  /* The 3D cover says what it shows (descriptions: Andre, 9 Oct 2026), changed only once that design is on the cover,
     so a map that fails to load never leaves it announcing a design it does not show (Astra, 9 Oct night). */
  _commitLabel(alt) {
    if (alt == null) return;
    this._alt = alt;
    if (this._g && this._g.canvas) this._g.canvas.setAttribute('aria-label', this._canvasLabel());
  }
  _canvasLabel() {
    return this._alt ? `${this._alt.replace(/\.?\s*$/, '.')} You can turn it with your finger or mouse.`
      : (this.dataset.canvasLabel || 'A cover you can turn with your finger or mouse');
  }
  async showMap({ url, base = 0, amp = 1, still, alt } = {}) {
    const my = ++this._seq;                        // only the latest call may change the cover (Astra review, 4 Oct)
    this._want = { url: url || '', base, amp };    // what the cover should show, for a rebuild after a lost context
    if (still) this.setStill(still, alt);
    const ok = await this.ready;
    if (my !== this._seq) return 'superseded';
    if (!ok || this._mode === 'still' || !this._g) return 'still';
    if (!url) { this._relief = null; this._reliefUrl = ''; this._applyRelief(); this._commitLabel(alt); return 'shown'; }
    let res;
    try { res = await this._loadMap(url, base, amp); } catch { return my === this._seq ? 'error' : 'superseded'; }
    if (my !== this._seq) return 'superseded';
    if (!this._g || this._mode === 'still') return 'still';
    this._relief = { heights: res.h, size: res.size, shift: 0 };   // range designs: the template's mapping
    this._reliefUrl = url;
    this._applyRelief();
    this._commitLabel(alt);
    window.__lastLoadMs = res.ms;                  // main-thread time to prepare a design, for phone tests
    return 'shown';
  }
  setRelief(heights, size, shift) {
    ++this._seq;                                   // a range map still loading must not overwrite a drawing
    this._want = null; this._reliefUrl = null;
    this._relief = heights ? { heights, size, shift } : null;
    this._applyRelief();
  }
  /* for tests */
  getDebugState() {
    return { mode: this._mode, canvases: this.querySelectorAll('canvas').length, seq: this._seq,
      relief: this._relief ? { size: this._relief.size, shift: this._relief.shift } : null,
      vertices: this._g ? this._g.outer.geometry.attributes.position.count : 0 };
  }

  /* ---------- 3D ---------- */
  async _start3d() {
    this._cancelIdle = null;
    const gen = this._gen;
    let mods;
    try { mods = await loadThree(this.dataset.threeSrc || DEFAULT_THREE, this.dataset.orbitSrc || DEFAULT_ORBIT); }
    catch { if (gen === this._gen) this._toStill('import'); return; }
    if (gen !== this._gen) return;
    try { this._build3d(mods); }
    catch { this._teardown3d(); this._toStill('webgl'); }
  }

  _build3d({ THREE, OrbitControls }) {
    const signal = this._ac.signal;
    const canvas = document.createElement('canvas');
    canvas.className = 'lsc-canvas';
    canvas.setAttribute('role', 'img');
    canvas.setAttribute('aria-label', this._canvasLabel());
    this._stage.prepend(canvas);
    const g = this._g = { THREE, canvas, mats: [], geoms: [], normalTex: null, dirty: true, onScreen: true, tween: null, firstCover: true };

    const renderer = g.renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));
    renderer.setClearColor(0x000000, 0);
    // Colour-true setup: a neutral tone curve and lights that travel with the viewer, so a surface facing you
    // shows the swatch colour whichever side you look at. No fixed "room" reflections.
    renderer.toneMapping = THREE.NeutralToneMapping;
    renderer.toneMappingExposure = 1.0;
    const scene = g.scene = new THREE.Scene();
    const camera = g.camera = new THREE.PerspectiveCamera(26, 1, 10, 5000);
    scene.add(camera);
    const key = new THREE.DirectionalLight(0xffffff, KEY);   // slightly above and left of the eye: shows the relief
    key.position.set(-0.35, 0.35, 1); key.target.position.set(0, 0, -1);
    camera.add(key, key.target);
    scene.add(new THREE.HemisphereLight(0xffffff, 0xd8d8d8, AMB));
    g.group = new THREE.Group(); scene.add(g.group);

    const controls = g.controls = new OrbitControls(camera, canvas);
    controls.enablePan = false; controls.enableDamping = true; controls.dampingFactor = 0.08;
    controls.autoRotateSpeed = 2.2; controls.minPolarAngle = 0.35; controls.maxPolarAngle = 2.5;
    controls.enableZoom = !this.hasAttribute('data-compact-phone');   // from the start, not only once sized (_applyTurn)
    g.onStart = () => { g.userMoved = true; if (this._hint) this._hint.hidden = true; };
    controls.addEventListener('start', g.onStart);

    this._setCover(this._buildCodeCover(), H_MM);

    // Lost context: back to the still image. Restored: build the cover again on a fresh canvas.
    // These listeners belong to this canvas only (g.ac), so the old canvas cannot touch the new cover.
    g.ac = new AbortController();
    const csig = AbortSignal.any ? AbortSignal.any([signal, g.ac.signal]) : g.ac.signal;
    canvas.addEventListener('webglcontextlost', (e) => { e.preventDefault(); if (this._g === g) this._toStill('lost'); }, { signal: csig });
    canvas.addEventListener('webglcontextrestored', () => {
      if (!this._live || this._g !== g) return;
      g.restored = true;
      this._teardown3d(); this._mode = 'loading'; this.dataset.state = 'loading';
      // a design chosen while the context was lost: start smooth, never under the old design's relief
      if (this._want && this._want.url !== (this._reliefUrl ?? '')) { this._relief = null; this._reliefUrl = ''; }
      this._ready = null; void this.ready; this._start3d();
    }, { signal: csig });

    const fit = () => {
      const w = this._stage.clientWidth, h = this._stage.clientHeight;
      if (!w || !h) return;
      this._applyTurn(g, canvas);
      renderer.setSize(w, h, false); camera.aspect = w / h; camera.updateProjectionMatrix(); g.dirty = true;
      // the framing depends on the stage height: redo it, unless the visitor has turned or zoomed the cover since
      if (!g.userMoved && !g.tween && !controls.autoRotate) this._setView(g.az ?? -Math.PI / 4, false);
    };
    fit();                                         // size it now; the observer follows later changes
    g.ro = new ResizeObserver(fit);
    g.ro.observe(this._stage);
    g.io = new IntersectionObserver((en) => { g.onScreen = en[0].isIntersecting; if (g.onScreen) g.dirty = true; });
    g.io.observe(this._stage);
    let shown = false;
    // draw only when something changed, and not while the cover is off screen (Astra review, 4 Oct)
    renderer.setAnimationLoop(() => {
      if (!g.onScreen) return;
      const tweening = g.tween !== null; this._tickTween();
      const moved = controls.update();
      if (g.dirty || tweening || moved || controls.autoRotate) {
        renderer.render(scene, camera); g.dirty = false;
        if (!shown) { shown = true; this._mode = '3d'; this.dataset.state = '3d'; }
      }
    });
    if (this._hint) { this._hint.textContent = this.dataset.hint || 'Drag to turn the cover'; this._hint.hidden = false; }
    if (this._views) this._views.hidden = false;
    this._mode = '3d-starting';
    if (this._resolveReady) this._resolveReady(true);
    // A rebuild (after a lost context) brings back the design the page last asked for, if it is not on yet.
    if (this._builds++ > 0 && this._want && this._want.url !== (this._reliefUrl ?? '')) this.showMap({ ...this._want });
  }

  _teardown3d() {
    const g = this._g; if (!g) return;
    this._g = null;
    if (g.ac) g.ac.abort();
    try {
      g.renderer.setAnimationLoop(null);
      g.ro && g.ro.disconnect(); g.io && g.io.disconnect();
      g.controls.removeEventListener('start', g.onStart);
      // OrbitControls (r165) puts a keydown listener on the canvas's root node (the document) and removes it from
      // whatever the root node is at dispose time. After a section reload the canvas is already detached, so put
      // it back in the document for a moment, or that listener would stay behind on every reload (found in the
      // local reload test, 5 Oct 2026).
      if (!g.canvas.isConnected) {
        g.canvas.style.display = 'none'; document.body.append(g.canvas);
        g.controls.dispose(); g.canvas.remove();
      } else g.controls.dispose();
      // After a lost-and-restored context the GPU objects belonged to the old context: deleting them only makes
      // the browser warn, so skip that and let them be collected.
      if (!g.restored) {
        for (const ge of g.geoms) ge.dispose();
        for (const m of g.mats) m.dispose();
        if (g.normalTex) g.normalTex.dispose();
        g.renderer.dispose();
      }
      g.renderer.forceContextLoss();               // free the WebGL context, or repeated reloads run out of them
    } catch { /* already gone */ }
    g.canvas.remove();
  }

  _toStill(reason) {
    if (this._g && reason === 'lost') { try { this._g.renderer.setAnimationLoop(null); } catch { /* ignore */ } }
    if (reason !== 'lost') this._teardown3d();
    this._mode = 'still';
    this.dataset.state = 'still';
    this.dataset.stillReason = reason;
    if (this._views) this._views.hidden = true;
    if (this._hint) {
      const msg = this.dataset.stillMessage || '';
      this._hint.textContent = msg; this._hint.hidden = !msg;
    }
    if (this._resolveReady) this._resolveReady(false);
  }

  _invalidate() { if (this._g) this._g.dirty = true; }

  _mat(opts = {}) {
    const m = new this._g.THREE.MeshStandardMaterial({ color: this._hex, roughness: 0.62, metalness: 0, ...opts });
    this._g.mats.push(m); return m;
  }
  _buildCodeCover() {
    const THREE = this._g.THREE;
    const [nu, nv, inu, inv] = this._phone ? [256, 320, 128, 80] : [384, 480, 192, 120];
    const m1 = this._mat(), m2 = this._mat({ roughness: 0.8, side: THREE.BackSide }), m3 = this._mat({ roughness: 0.7, side: THREE.DoubleSide });
    const geoms = [buildSurface(THREE, 0, nu, nv), buildSurface(THREE, 3.2, inu, inv), buildRim(THREE, true, nu), buildRim(THREE, false, nu)];
    this._g.geoms.push(...geoms);
    const outer = new THREE.Mesh(geoms[0], m1);
    return { meshes: [outer, new THREE.Mesh(geoms[1], m2), new THREE.Mesh(geoms[2], m3), new THREE.Mesh(geoms[3], m3)], outer, outerMat: m1 };
  }
  _setCover(c, h) {
    const g = this._g;
    c.meshes.forEach((m) => g.group.add(m));
    g.outer = c.outer; g.outerMat = c.outerMat;
    g.outerBase = g.outer.geometry.attributes.position.array.slice();
    g.outerNormals = g.outer.geometry.attributes.normal.array.slice();
    g.coverH = h; g.dirty = true;
    g.controls.target.set(0, h * 0.5, 0);
    g.controls.minDistance = h * 1.1; g.controls.maxDistance = h * 4.5;
    this._setView(-Math.PI / 4, false);              // three-quarter: front and outer side
    this._applyRelief();
  }

  _sampleH(r, u, v) {          // bilinear; u across, v from the top (prototype sampleH)
    const HN = r.size, H = r.heights;
    const x = Math.min(HN - 1.001, Math.max(0, u * (HN - 1))), y = Math.min(HN - 1.001, Math.max(0, v * (HN - 1)));
    const x0 = x | 0, y0 = y | 0, fx = x - x0, fy = y - y0, i = y0 * HN + x0;
    return (H[i] * (1 - fx) + H[i + 1] * fx) * (1 - fy) + (H[i + HN] * (1 - fx) + H[i + HN + 1] * fx) * fy;
  }
  _applyRelief() {
    const g = this._g; if (!g || !g.outer) return;
    const r = this._relief;
    const pos = g.outer.geometry.attributes.position, uv = g.outer.geometry.attributes.uv.array, a = pos.array;
    const base = g.outerBase, nrm = g.outerNormals;
    if (!r) a.set(base);
    else {
      for (let k = 0, m = 0; k < a.length; k += 3, m += 2) {
        const h = this._sampleH(r, (uv[m] - r.shift + 1) % 1, 1 - uv[m + 1]) * RELIEF;
        a[k] = base[k] + nrm[k] * h;
        a[k + 1] = base[k + 1] + nrm[k + 1] * h;
        a[k + 2] = base[k + 2] + nrm[k + 2] * h;
      }
    }
    pos.needsUpdate = true;
    // smooth base normals on the mesh; the fine relief comes from a normal map (sharp edges without a heavier mesh)
    g.outer.geometry.attributes.normal.array.set(nrm); g.outer.geometry.attributes.normal.needsUpdate = true;
    this._updateNormalMap();
    g.dirty = true;
  }
  _updateNormalMap() {
    const g = this._g, THREE = g.THREE, r = this._relief, m = g.outerMat;
    if (!r) {
      if (m.normalMap) { m.normalMap = null; m.needsUpdate = true; }
      return;
    }
    const HN = r.size, H = r.heights;
    const n = Math.min(HN, this._phone ? 1024 : 2048), k = HN / n;   // phones: a smaller map (memory)
    const at = (x, y) => H[Math.min(HN - 1, Math.round(y * k)) * HN + Math.min(HN - 1, Math.round(x * k))];
    const data = new Uint8Array(n * n * 4);
    const du = (2 * Math.PI * 50) / n, dv = g.coverH / n;            // mm per pixel round the leg and up it
    for (let y = 0; y < n; y++) {
      const yu = Math.max(0, y - 1), yd = Math.min(n - 1, y + 1), row = (n - 1 - y) * n;
      for (let x = 0; x < n; x++) {
        const xl = (x - 1 + n) % n, xr = (x + 1) % n;
        const gx = (at(xr, y) - at(xl, y)) * 0.5 * RELIEF / du;
        const gy = (at(x, yu) - at(x, yd)) * 0.5 * RELIEF / dv;         // slope upwards
        let nx = -gx, ny = -gy, nz = 1; const l = Math.hypot(nx, ny, nz); nx /= l; ny /= l; nz /= l;
        const o = (row + x) * 4;
        data[o] = (nx * 0.5 + 0.5) * 255; data[o + 1] = (ny * 0.5 + 0.5) * 255; data[o + 2] = (nz * 0.5 + 0.5) * 255; data[o + 3] = 255;
      }
    }
    if (!g.normalTex || g.normalTex.image.width !== n) {
      if (g.normalTex) g.normalTex.dispose();
      g.normalTex = new THREE.DataTexture(data, n, n, THREE.RGBAFormat);
      g.normalTex.wrapS = THREE.RepeatWrapping; g.normalTex.wrapT = THREE.ClampToEdgeWrapping;
      g.normalTex.magFilter = THREE.LinearFilter; g.normalTex.minFilter = THREE.LinearMipmapLinearFilter; g.normalTex.generateMipmaps = true;
      g.normalTex.colorSpace = THREE.NoColorSpace;
    } else g.normalTex.image.data.set(data);
    g.normalTex.offset.x = -r.shift; g.normalTex.needsUpdate = true;
    if (m.normalMap !== g.normalTex) { m.normalMap = g.normalTex; m.normalScale.set(1, 1); m.needsUpdate = true; }
  }

  _loadMap(url, base, amp) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';              // maps come from Shopify Files; the canvas must stay readable
      img.decoding = 'async';
      img.onerror = () => reject(new Error('map'));
      img.onload = () => {
        // a failure here (an unreadable canvas, out of memory) must reject, not leave the load waiting for ever
        // (Astra, 6 Oct 2026); the canvas and image are released either way
        const t0 = performance.now();
        const c = document.createElement('canvas');
        try {
          const S = Math.min(2048, img.naturalWidth || 1024);     // the design's own resolution
          c.width = c.height = S;
          const x = c.getContext('2d', { willReadFrequently: true }); x.imageSmoothingQuality = 'high'; x.drawImage(img, 0, 0, S, S);
          const px = x.getImageData(0, 0, S, S).data, h = new Float32Array(S * S);
          for (let i = 0; i < h.length; i++) h[i] = base + amp * px[i * 4] / 255;   // shallow textures are stored stretched
          resolve({ h, size: S, ms: Math.round(performance.now() - t0) });
        } catch (e) {
          reject(e);
        } finally {
          c.width = c.height = 0; img.src = '';                                   // release memory now
        }
      };
      img.src = url;
    });
  }

  /* ---------- views ---------- */
  _onView(e) {
    const b = e.target.closest('button[data-view]'); if (!b || !this._g) return;
    const v = b.dataset.view, g = this._g;
    if (v === 'spin') {
      g.controls.autoRotate = !g.controls.autoRotate; b.setAttribute('aria-pressed', String(g.controls.autoRotate)); return;
    }
    const az = { front: 0, side: -Math.PI / 2, back: Math.PI }[v];
    this._setView(az, true);
    for (const o of this._views.querySelectorAll('button[data-view]')) if (o.dataset.view !== 'spin') o.setAttribute('aria-pressed', String(o === b));
  }
  /* Phones, where the page asks for it (data-compact-phone, the chooser): turn left and right only, at the usual
     slight downward look, and let a vertical swipe scroll the page; no view buttons, so the cover can fill the stage
     (Andre, 9 Oct 2026). Checked on every resize, so turning a tablet round follows. */
  _compact() { return this.hasAttribute('data-compact-phone') && window.matchMedia('(max-width: 899px)').matches; }
  /* Launch morning (Andre, 10 Oct 2026): on both pages the cover turns left and right only, on desktop too (people look
     round a leg cover, not over it); the view buttons stay on desktop. */
  _applyTurn(g, canvas) {
    const lock = this.hasAttribute('data-compact-phone');
    g.controls.minPolarAngle = lock ? Math.PI / 2 - EL : 0.35;
    g.controls.maxPolarAngle = lock ? Math.PI / 2 - EL : 2.5;
    // no pinch or wheel zoom either: the cover stays at the size framed for it, so nobody shrinks it and loses it,
    // and the wheel scrolls the page (Andre, 10 Oct 2026, after launch, on his phone)
    g.controls.enableZoom = !lock;
    canvas.style.touchAction = lock ? 'pan-y' : '';
  }
  _setView(az, animate) {
    const g = this._g, THREE = g.THREE;
    // Fill the stage between the hint (top) and the view buttons (bottom), at most 84% of its height:
    // closer than the old fixed 3.0 cover heights on desktop (Andre, 6 Oct), and never behind the buttons.
    // On a compact phone there are no buttons: fill up to 92%.
    // Launch morning (Andre): a bit closer on desktop too, for the pages that ask for it: the cover may run up under
    // the drag hint (it goes after the first drag), still clear of the view buttons.
    const compact = this._compact(), close = this.hasAttribute('data-compact-phone');
    const hpx = this._stage.clientHeight || 600, el = EL;
    // room is kept only for what the page's layout shows: the view buttons and hint can be hidden by the page (narrow
    // columns). Asked of the layout, not of the moment: they switch on only once the 3D cover is up, after this runs.
    const layoutShows = (n) => {
      if (!n) return false;
      const was = n.hidden; n.hidden = false;
      const cs = getComputedStyle(n), on = cs.display !== 'none' && cs.opacity !== '0';   // opacity 0: keyboard-only buttons
      n.hidden = was;
      return on;
    };
    const buttons = layoutShows(this._views), hint = layoutShows(this._hint);
    const padTop = compact || !hint ? 10 : close ? 14 : 34, padBottom = compact || !buttons ? 8 : close ? 56 : 64;
    const fill = Math.min(compact || close ? 0.92 : 0.84, Math.max(0.6, (hpx - padTop - padBottom) / hpx));
    const span = g.coverH / fill, dist = span / (2 * Math.tan(THREE.MathUtils.degToRad(g.camera.fov / 2)));
    const ty = g.coverH * 0.5 - span * (padBottom - padTop) / (2 * hpx);   // centred in the band, not the stage
    g.controls.target.set(0, ty, 0); g.az = az; g.userMoved = false;
    const end = new THREE.Vector3(Math.sin(az) * Math.cos(el) * dist, ty + Math.sin(el) * dist, Math.cos(az) * Math.cos(el) * dist);
    if (!animate || this._reduceMotion) { g.camera.position.copy(end); g.camera.lookAt(g.controls.target); g.dirty = true; return; }
    g.tween = { from: g.camera.position.clone(), to: end, t: 0 };
  }
  _tickTween() {
    const g = this._g, tw = g.tween; if (!tw) return;
    const THREE = g.THREE;
    tw.t = Math.min(1, tw.t + 0.045);
    const e = tw.t < 0.5 ? 2 * tw.t * tw.t : 1 - Math.pow(-2 * tw.t + 2, 2) / 2;
    const a = new THREE.Spherical().setFromVector3(tw.from.clone().sub(g.controls.target));
    const b = new THREE.Spherical().setFromVector3(tw.to.clone().sub(g.controls.target));
    let dth = b.theta - a.theta; if (dth > Math.PI) dth -= 2 * Math.PI; if (dth < -Math.PI) dth += 2 * Math.PI;
    const s = new THREE.Spherical(a.radius + (b.radius - a.radius) * e, a.phi + (b.phi - a.phi) * e, a.theta + dth * e);
    g.camera.position.setFromSpherical(s).add(g.controls.target);
    if (tw.t >= 1) g.tween = null;
  }
}

if (!customElements.get('legstyle-cover-viewer')) customElements.define('legstyle-cover-viewer', LegStyleCoverViewer);
